import { useState, useEffect } from 'react'
import { Users, Monitor, Bot, ArrowRight } from 'lucide-react'
import { Link } from 'react-router-dom'
import { AreaChart, Area, ResponsiveContainer } from 'recharts'
import {
  getAdminAnalytics,
  getAnalyticsTrends,
  listUsers,
  listWorkspaces,
  type AdminAnalytics,
  type TrendPoint,
} from '../../lib/api'
import { CHART_PALETTE } from '../../lib/chart-colors'

interface Stats {
  users: number
  workspaces: number
}

function Sparkline({ data, dataKey, color }: { data: TrendPoint[]; dataKey: string; color: string }) {
  if (!data || data.length < 2) return null
  return (
    <div className="h-10 w-28">
      <ResponsiveContainer width="100%" height="100%">
        <AreaChart data={data} margin={{ top: 2, right: 2, bottom: 2, left: 2 }}>
          <Area
            type="monotone"
            dataKey={dataKey}
            stroke={color}
            fill={color}
            fillOpacity={0.15}
            strokeWidth={1.5}
            dot={false}
            isAnimationActive={false}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  )
}

export default function AdminOverview() {
  const [stats, setStats] = useState<Stats>({ users: 0, workspaces: 0 })
  const [analytics, setAnalytics] = useState<AdminAnalytics | null>(null)
  const [trends, setTrends] = useState<TrendPoint[]>([])
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    async function loadStats() {
      try {
        const [users, workspaces, analyticsData, trendsData] = await Promise.all([
          listUsers(),
          listWorkspaces(),
          getAdminAnalytics('30d').catch(() => null),
          getAnalyticsTrends('7d').catch(() => null),
        ])
        setStats({
          users: users.length,
          workspaces: workspaces.length,
        })
        if (analyticsData) setAnalytics(analyticsData)
        if (trendsData) setTrends(trendsData)
      } catch (err) {
        console.error('Failed to load stats:', err)
      } finally {
        setLoading(false)
      }
    }
    loadStats()
  }, [])

  const metricCards = analytics
    ? [
        {
          label: 'Active Users (DAU)',
          value: analytics.platform.active_users_daily,
          sparklineKey: 'active_users',
          color: CHART_PALETTE[0],
          to: '/admin/analytics/users',
        },
        {
          label: 'Total Apps',
          value: analytics.platform.total_apps,
          sparklineKey: 'total_apps',
          color: CHART_PALETTE[1],
          to: '/admin/analytics/apps',
        },
        {
          label: 'Running Apps',
          value: analytics.platform.running_apps,
          sparklineKey: 'deploys',
          color: CHART_PALETTE[2],
          to: '/admin/analytics/apps',
        },
        {
          label: 'Running Ratio',
          value: `${analytics.platform.running_app_ratio}%`,
          sparklineKey: null,
          color: CHART_PALETTE[3],
          to: '/admin/analytics',
        },
      ]
    : []

  const quickCards = [
    { label: 'Workspaces', value: stats.workspaces, icon: Monitor, href: '/admin/workspaces' },
    { label: 'Users', value: stats.users, icon: Users, href: '/admin/users' },
  ]

  return (
    <div>
      <div className="mb-8">
        <h1 className="text-2xl font-bold mb-2">Admin Overview</h1>
        <p className="text-muted-foreground">Platform management and configuration</p>
      </div>

      {loading ? (
        <div className="flex items-center justify-center h-64">
          <div className="animate-spin rounded-full h-8 w-8 border-t-2 border-b-2 border-primary"></div>
        </div>
      ) : (
        <>
          {/* Analytics metric cards with sparklines */}
          {analytics && (
            <div className="mb-8">
              <h2 className="text-lg font-semibold mb-4">Platform Metrics</h2>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                {metricCards.map((card) => (
                  <Link
                    key={card.label}
                    to={card.to}
                    className="p-4 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors block"
                  >
                    <div className="flex items-center justify-between mb-1">
                      <span className="text-sm text-muted-foreground">{card.label}</span>
                      <ArrowRight className="w-3.5 h-3.5 text-muted-foreground" />
                    </div>
                    <div className="flex items-end justify-between gap-3">
                      <span className="text-2xl font-bold">{card.value}</span>
                      {card.sparklineKey && (
                        <Sparkline data={trends} dataKey={card.sparklineKey} color={card.color} />
                      )}
                    </div>
                  </Link>
                ))}
              </div>
            </div>
          )}

          {/* Workspace/User count cards */}
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 mb-8">
            {quickCards.map((card) => (
              <Link
                key={card.label}
                to={card.href}
                className="p-6 rounded-xl border border-border bg-card hover:border-primary/50 transition-colors block"
              >
                <div className="flex items-center justify-between mb-4">
                  <card.icon className="w-8 h-8 text-muted-foreground" />
                  <span className="text-2xl font-bold">{card.value}</span>
                </div>
                <p className="text-muted-foreground">{card.label}</p>
              </Link>
            ))}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-4">Quick Actions</h2>
              <div className="space-y-2">
                <Link
                  to="/admin/workspaces"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <Monitor className="w-5 h-5 text-muted-foreground" />
                  <span>Manage Workspaces</span>
                </Link>
                <Link
                  to="/admin/users"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <Users className="w-5 h-5 text-muted-foreground" />
                  <span>Manage Users</span>
                </Link>
                <Link
                  to="/admin/claude-code"
                  className="flex items-center gap-3 p-3 rounded-lg hover:bg-secondary transition-colors"
                >
                  <Bot className="w-5 h-5 text-muted-foreground" />
                  <span>Claude Code Configuration</span>
                </Link>
              </div>
            </div>

            <div className="p-6 rounded-xl border border-border bg-card">
              <h2 className="text-lg font-semibold mb-4">Platform Info</h2>
              <div className="space-y-3 text-sm">
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Platform</span>
                  <span>CC Platform v1.0</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Workspace Image</span>
                  <span>cc-workspace:latest</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">Memory Limit</span>
                  <span>4GB per workspace</span>
                </div>
                <div className="flex justify-between">
                  <span className="text-muted-foreground">CPU Limit</span>
                  <span>2 cores per workspace</span>
                </div>
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  )
}
